'use client';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { Routes } from '@/app/enums';
import { PropsWithClassname } from '@/app/types';
import { ProjectPhoto, SubprojectStateStatusEnum } from '@/app/api';

import Button, { ButtonHTMLTag, ButtonSize, ButtonStyle } from '@/app/components/Button/Button';
import HandshakeIcon from '@/app/components/HandshakeIcon/HandshakeIcon';
import Link from 'next/link';
import Notification from '@/app/components/Notification/Notification';
import SubprojectList from '@/app/components/SubprojectList/SubprojectList';

interface SubprojectTabProps extends PropsWithClassname {
  projectId: string;
  projectNumericId: number;
  projectUrl: string;
  projectMainImage?: ProjectPhoto;
  isActive: boolean;
}

const SubprojectTab = ({
  projectId,
  projectNumericId,
  projectUrl,
  projectMainImage,
  isActive,
  className,
}: SubprojectTabProps) => {
  const { t } = useTranslation();
  const [activeSubprojectsTotal, setActiveSubprojectsTotal] = useState<number | null>(null);
  const [finishedSubprojectsTotal, setFinishedSubprojectsTotal] = useState<number | null>(null);

  return (
    <div className={classNames(className, '')}>
      <h2 className="mb-[20px] text-[32px] font-medium leading-[40px] text-balance">
        {t('components.ProjectChallengeTab.heading')}
      </h2>
      <p className="mb-[40px] text-[16px]">{t('components.ProjectChallengeTab.introduction')}</p>
      <h3 className="mb-[20px] text-[24px] leading-[32px] font-medium text-balance">
        {t('components.ProjectChallengeTab.howItWorksHeading')}
      </h3>
      <ol className="list-decimal pl-[40px] mb-[20px]">
        {(t('components.ProjectChallengeTab.howItWorksSteps', { returnObjects: true }) as Array<string>).map(
          (stepText, index) => (
            <li key={index}>{stepText}</li>
          ),
        )}
      </ol>
      {isActive && (
        <div className="flex mb-[40px] flex-col md:flex-row gap-[24px]">
          <Button
            className="w-max"
            size={ButtonSize.LARGE}
            tagSpecificProps={{
              htmlTag: ButtonHTMLTag.LINK,
              href: `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.NEW_CHALLENGE}/${projectNumericId}`,
            }}
          >
            <HandshakeIcon className="inline mr-[12px]" />
            {t('components.ProjectChallengeTab.addChallenge')}
          </Button>
          <Button
            className="w-max"
            size={ButtonSize.LARGE}
            style={ButtonStyle.PRIMARY_OUTLINED}
            tagSpecificProps={{
              htmlTag: ButtonHTMLTag.LINK,
              href: `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.CHALLENGE}`,
            }}
          >
            {t('components.ProjectChallengeTab.moreInfo')}
          </Button>
        </div>
      )}
      {!isActive && <Notification>{t('components.ProjectChallengeTab.projectFinishedDisclaimer')}</Notification>}
      {isActive && activeSubprojectsTotal === 0 && finishedSubprojectsTotal === 0 && (
        <>
          <h2 className="mb-[20px] text-[32px] font-medium leading-[40px] text-balance">
            {t('components.ProjectChallengeTab.noChallengesYet')}
          </h2>
          <Link
            href={`${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.NEW_CHALLENGE}/${projectNumericId}`}
            prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
            className="flex flex-col justify-center items-center w-[268px] h-[368px] p-[16px] grow-0 shrink-0 bg-white rounded border border-dashed hover:border-solid border-primary-mid shadow-none hover:shadow-xl transition-all duration-500"
          >
            <span className="w-[80px] h-[80px] flex justify-center items-center bg-primary-light rounded-full mb-[40px]">
              <HandshakeIcon className="text-primary-mid w-[42px] h-[42px]" />
            </span>
            <h3 className="mb-[32px] text-[24px] text-center leading-[32px] font-medium text-balance">
              {t('components.ProjectChallengeTab.beTheFirst')}
            </h3>
            <span className="text-center block text-primary-mid uppercase text-[12px] tracking-[4.5px] font-medium leading-[24px]">
              + {t('components.ProjectChallengeTab.addChallenge')}
            </span>
          </Link>
        </>
      )}
      {isActive && (
        <>
          <SubprojectList
            projectId={projectId}
            projectUrl={projectUrl}
            projectMainImage={projectMainImage}
            statuses={[SubprojectStateStatusEnum.Active]}
            title={t('components.ProjectChallengeTab.ongoingChallenges')}
            onTotalCountChange={setActiveSubprojectsTotal}
          />
          <SubprojectList
            projectId={projectId}
            projectUrl={projectUrl}
            projectMainImage={projectMainImage}
            statuses={[SubprojectStateStatusEnum.Finished]}
            title={t('components.ProjectChallengeTab.closedChallenges')}
            onTotalCountChange={setFinishedSubprojectsTotal}
          />
        </>
      )}
    </div>
  );
};

export default SubprojectTab;
